import { readPreSpecialRate, sumTripRevenue } from './tripRevenue';
import { getUnassignedReasonChartKeys, normalizeUnassignedReason } from './normalizeUnassignedReason';
import { formatFleetClock, isFleetSession } from './fleetWindows';

const DEFAULT_TZ = 'America/Los_Angeles';

function isBreak(trip) {
  return Boolean(trip?.is_break || trip?.isBreakTrip);
}

export function assignedTrips(drivers) {
  const trips = [];
  for (const driver of drivers || []) {
    for (const trip of driver?.trips || []) {
      if (!isBreak(trip)) trips.push(trip);
    }
  }
  return trips;
}

export function openTrips(unassignedTrips) {
  return (unassignedTrips || []).filter((trip) => trip && !isBreak(trip));
}

export function companyTimezone(assignmentData) {
  const raw = String(assignmentData?.metadata?.timezone || '').trim();
  if (!raw || raw === 'None') return DEFAULT_TZ;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: raw });
    return raw;
  } catch {
    return DEFAULT_TZ;
  }
}

/** Fleet plan from metadata, or from drivers when the charts payload has no metadata chunk. */
export function sessionIsFleetPlan(assignmentData) {
  if (isFleetSession(assignmentData)) return true;
  return (assignmentData?.drivers || []).some(
    (driver) => driver?.planning_mode === 'fleet'
      || (Array.isArray(driver?.fleet_windows) && driver.fleet_windows.length > 0)
  );
}

export function tripLosLabel(trip) {
  const los = String(trip?.los_title || trip?.losTitle || '').trim();
  const space = String(trip?.space_type_title || trip?.spaceTypeTitle || '').trim();
  if (los && space) return `${los} (${space})`;
  if (los) return los;
  if (space) return space;
  const fallback = String(trip?.space_type || trip?.spaceType || '').trim();
  return fallback || 'Unknown';
}

export function tripScheduleIso(trip) {
  return trip?.schedule_time || trip?.scheduleTime || '';
}

export function buildRevenueSummary(drivers, unassignedTrips) {
  const assigned = assignedTrips(drivers);
  const open = openTrips(unassignedTrips);
  const plan = sumTripRevenue(assigned);
  const unassigned = sumTripRevenue(open);
  const missing = [...assigned, ...open].filter((trip) => readPreSpecialRate(trip) == null).length;
  const session = plan == null && unassigned == null
    ? null
    : Math.round(((plan || 0) + (unassigned || 0)) * 100) / 100;
  const capture = session != null && session > 0
    ? ((plan || 0) / session) * 100
    : null;
  return { plan, unassigned, session, missing, capture };
}

function addMoney(bucket, key, amount) {
  if (!bucket.has(key)) bucket.set(key, 0);
  bucket.set(key, Math.round((bucket.get(key) + amount) * 100) / 100);
}

export function revenueByLos(drivers, unassignedTrips) {
  const assigned = new Map();
  const unassigned = new Map();
  for (const trip of assignedTrips(drivers)) {
    const rate = readPreSpecialRate(trip);
    if (rate == null) continue;
    addMoney(assigned, tripLosLabel(trip), rate);
  }
  for (const trip of openTrips(unassignedTrips)) {
    const rate = readPreSpecialRate(trip);
    if (rate == null) continue;
    addMoney(unassigned, tripLosLabel(trip), rate);
  }
  const labels = [...new Set([...assigned.keys(), ...unassigned.keys()])];
  labels.sort((a, b) => (
    ((unassigned.get(b) || 0) + (assigned.get(b) || 0))
    - ((unassigned.get(a) || 0) + (assigned.get(a) || 0))
  ));
  return labels.map((label) => ({
    label,
    assigned: assigned.get(label) || 0,
    unassigned: unassigned.get(label) || 0,
  }));
}

export function unassignedRevenueByReason(unassignedTrips) {
  const totals = new Map();
  for (const trip of openTrips(unassignedTrips)) {
    const rate = readPreSpecialRate(trip);
    if (rate == null) continue;
    for (const reason of getUnassignedReasonChartKeys(trip.reason)) {
      addMoney(totals, reason, rate);
    }
  }
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([label, value]) => ({ label, value }));
}

export function highestUnassignedTrips(unassignedTrips, limit = 5) {
  const rated = openTrips(unassignedTrips)
    .filter((trip) => readPreSpecialRate(trip) != null)
    .sort((a, b) => readPreSpecialRate(b) - readPreSpecialRate(a) || String(a.trip_id || '').localeCompare(String(b.trip_id || '')));
  if (limit == null || limit === 'all') return rated;
  const n = Number(limit);
  if (!Number.isFinite(n) || n < 0) return rated;
  return rated.slice(0, n);
}

function hourParts(iso, timezone) {
  if (iso == null || iso === '') return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || DEFAULT_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const pick = (type) => parts.find((part) => part.type === type)?.value || '';
  const year = pick('year');
  const month = pick('month');
  const day = pick('day');
  let hour = pick('hour');
  if (hour === '24') hour = '00';
  if (!year || !month || !day || hour === '') return null;
  return { year, month, day, hour, sort: `${year}${month}${day}${hour}` };
}

function windowHours(size) {
  const n = Math.floor(Number(size));
  if (!Number.isFinite(n)) return 3;
  return Math.max(1, Math.min(48, n));
}

function pickupWindow(hour, size) {
  const span = windowHours(size);
  const start = Math.floor(Number(hour.hour) / span) * span;
  const end = Math.min(24, start + span);
  return {
    ...hour,
    start,
    end,
    sort: `${hour.year}${hour.month}${hour.day}${String(start).padStart(2, '0')}`,
  };
}

/** Assigned and unassigned dollars by pickup time. `size` is hours per window, at least 1. */
export function revenueByHour(drivers, unassignedTrips, timezone = DEFAULT_TZ, size = 3) {
  const assigned = new Map();
  const unassigned = new Map();
  const meta = new Map();
  const take = (trip, bucket) => {
    const rate = readPreSpecialRate(trip);
    if (rate == null) return;
    const hour = hourParts(tripScheduleIso(trip), timezone);
    if (!hour) return;
    const window = pickupWindow(hour, size);
    addMoney(bucket, window.sort, rate);
    meta.set(window.sort, window);
  };
  assignedTrips(drivers).forEach((trip) => take(trip, assigned));
  openTrips(unassignedTrips).forEach((trip) => take(trip, unassigned));
  const keys = [...meta.keys()].sort();
  const days = new Set([...meta.values()].map((window) => `${window.year}${window.month}${window.day}`));
  const multiDay = days.size > 1;
  return keys.map((key) => {
    const window = meta.get(key);
    const clock = `${window.start}-${window.end}`;
    return {
      label: multiDay ? `${Number(window.month)}/${Number(window.day)} ${clock}` : clock,
      assigned: assigned.get(key) || 0,
      unassigned: unassigned.get(key) || 0,
    };
  });
}

export function revenueByDriver(drivers) {
  return (drivers || []).map((driver) => {
    const total = sumTripRevenue(driver?.trips);
    return {
      label: driver?.driver_name || 'Driver',
      value: total == null ? 0 : total,
      hasRate: total != null,
    };
  });
}

function blankRig(value, los) {
  const text = String(value || '').trim();
  if (!text || text === '—' || text === '-' || text.toLowerCase() === 'n/a') return true;
  if (los && text.toLowerCase() === String(los).trim().toLowerCase()) return true;
  return false;
}

export function revenueByFleet(drivers, timezone = DEFAULT_TZ) {
  const rows = [];
  for (const driver of drivers || []) {
    const windows = [...(driver?.fleet_windows || [])].sort(
      (a, b) => (a.window_index ?? 0) - (b.window_index ?? 0)
    );
    if (!windows.length && driver?.planning_mode !== 'fleet') continue;
    const name = String(driver?.driver_name || 'Driver').split(' ')[0] || 'Driver';
    const byIndex = new Map();
    windows.forEach((window, index) => {
      const los = String(window?.los_title || '').trim();
      const rig = blankRig(window?.rig_no || window?.fleet_id, los)
        ? `V${index + 1}`
        : String(window.rig_no || window.fleet_id).trim();
      const start = formatFleetClock(window?.start, timezone);
      const end = formatFleetClock(window?.end, timezone);
      const clock = start && end ? `${start}–${end}` : '';
      byIndex.set(Number(window.window_index), {
        label: [name, rig, clock].filter(Boolean).join(' · '),
        trips: [],
      });
    });
    const loose = [];
    for (const trip of driver?.trips || []) {
      if (isBreak(trip)) continue;
      const idx = trip?.fleet_window_index;
      const bucket = idx == null || idx === '' ? null : byIndex.get(Number(idx));
      if (bucket) bucket.trips.push(trip);
      else loose.push(trip);
    }
    for (const bucket of byIndex.values()) {
      const total = sumTripRevenue(bucket.trips);
      if (total == null) continue;
      rows.push({ label: bucket.label, value: total, hasRate: true });
    }
    const looseTotal = sumTripRevenue(loose);
    if (looseTotal != null) {
      rows.push({ label: `${name} · No window`, value: looseTotal, hasRate: true });
    }
  }
  return rows;
}

export function unassignedReasonLabel(trip) {
  return normalizeUnassignedReason(trip?.reason);
}
